import { Component } from '@angular/core';
import { KaFeld } from './ka-felder';
import { HlAreaComponent } from '../../detail/hl-area/hl-area.component';
import { verfahrenVon } from '../verfahren';
import { KaAufnahme } from './ka-modell';
import {
	KaMaske,
	KaMaskeId,
	kaFeldName,
	kaFeldText,
	kaFeldZahl,
	kaMaske,
	kaMaskenFelder,
	istKaPflichtfeld,
	schreibeKaFeld,
} from './ka-maske';

/**
 * Gemeinsamer Unterbau der vier KA-Eingabemasken.
 *
 * Wie jede Maske der Detailansicht erbt sie von {@link HlAreaComponent} - daran
 * haengen der Punkt (`data`), die Validierung und der Speichern-Zustand - und legt
 * ihren Inhalt in `<bp-hl-area-container>`. Die Bauteile selbst (und damit ihre
 * Vorlagen) bleiben getrennt: Die Bloecke des Bogens sind verschieden gebaut, und
 * die Vollaufnahme wird sie weiter auseinanderziehen.
 *
 * **Sie schreibt nur in KA-Punkte.** Die Adresse einer Maske liesse sich von Hand
 * eintippen; bei einem SEA-Punkt oder einem Punkt mit unbekanntem Verfahren zeigt
 * die Maske deshalb einen Hinweis statt der Felder (Regel 3 in `verfahren.ts`).
 * Sonst entstuende ein Zwitter, der sich weder pruefen noch drucken laesst.
 */
@Component({
	standalone: false,
	template: '',
	styles: [''],
})
export class KaMaskeComponent extends HlAreaComponent {
	/**
	 * Welche Maske das ist. Jede Unterklasse nennt ihre Kennung; die Grundklasse
	 * selbst wird nie gezeichnet.
	 *
	 * Bewusst ein Lese-Zugriff und kein Feld: Ein Feld entsteht erst im
	 * Konstruktor. Die Tests dieses Ordners bauen ihre Bauteile ohne Konstruktor
	 * (`Object.create(...)`, weil an einem solchen Bauteil zwanzig Dienste haengen),
	 * und dann waere die Kennung leer.
	 */
	protected get maskeId(): KaMaskeId {
		throw new Error('Jede KA-Maske muss ihre Kennung nennen (maskeId).');
	}

	get maske(): KaMaske {
		return kaMaske(this.maskeId);
	}

	/** Beschriftung des Bearbeiten-Fensters. */
	get titel(): string {
		return this.maske.titel;
	}

	/** Die Felder dieser Maske, in der Reihenfolge des Bogens. */
	get felder(): KaFeld[] {
		return kaMaskenFelder(this.maskeId);
	}

	/** Ein KA-Punkt? Nur dann zeigt die Maske Felder. */
	get istKaPunkt(): boolean {
		return verfahrenVon(this.data) === 'ka' && !!this.data?.ka;
	}

	get aufnahme(): KaAufnahme | undefined {
		return this.data?.ka;
	}

	name(feld: KaFeld, zeile?: number): string {
		return kaFeldName(feld, zeile);
	}

	/**
	 * Beschriftung wie im Bogen: Feldnummer, dann Name.
	 *
	 * Wird hier zusammengesetzt und nicht in der Vorlage: Angular entfernt beim
	 * Bauen getippte Leerzeichen zwischen zwei Ausdrücken, und dann klebte die
	 * Nummer am Namen.
	 */
	beschriftung(feld: KaFeld): string {
		return feld.nr ? `${feld.nr} ${feld.name}` : feld.name;
	}

	pflicht(feld: KaFeld): boolean {
		return istKaPflichtfeld(feld);
	}

	text(feld: KaFeld, zeile?: number): string {
		return kaFeldText(this.data, feld, zeile);
	}

	zahl(feld: KaFeld, zeile?: number): number | undefined {
		return kaFeldZahl(this.data, feld, zeile);
	}

	setze(feld: KaFeld, wert: unknown, zeile?: number): void {
		schreibeKaFeld(this.data, feld, wert, zeile);
	}

	/**
	 * Was unter jeder Maske steht.
	 *
	 * Die beiden Kennzeichen nennt der amtliche Bogen auf jeder seiner Seiten; sie
	 * sind hier keine Zierde, sondern die einzige Art, "nachgesehen, da ist nichts"
	 * von "nicht nachgesehen" zu unterscheiden (siehe `ka-modell.ts`). Der Satz zu
	 * den Listen sagt, warum die Felder noch freie Eingaben sind.
	 */
	get fussText(): string {
		return 'Pflichtfelder sind die Felder der Kurzaufnahme (verkürzter Datensatz). '
			+ 'Die Schlüssellisten der Kartieranleitung liegen noch nicht vor, alle Listenfelder sind '
			+ 'vorerst freie Eingaben. Kennzeichen wie im Bogen: nv = nicht vorhanden, nb = nicht bestimmt.';
	}

	/**
	 * Hinweis auf mehrere Eintraege. Der Bogen gibt je Horizont drei Schreibzeilen
	 * vor; solange die Eingabe freier Text ist, werden Eintraege mit Semikolon
	 * getrennt (siehe `kaEintraegeAlsText`).
	 */
	hinweis(feld: KaFeld): string {
		const nr = feld.nr ? `Feld ${feld.nr} des Bogens` : 'ohne Feldnummer im Bogen';
		return feld.mehrfach ? `${nr} – mehrere Einträge mit Semikolon trennen` : nr;
	}
}
